import { DataTable, Icon, type ColumnDef, SectionCard } from "@/deps/ui-library";
import { useRoles } from "@/api/queries";
import type { RoleDefinition } from "@/api/types";
import { PERMISSIONS, ROLE_NAMES, ROLE_PERMISSIONS, type Permission } from "@/lib/permissions";

/**
 * The roles matrix, rendered from the permission catalogue rather than a copy of it.
 *
 * `lib/permissions.ts` is checked against the C# catalogue by a build-time test, so this table
 * cannot show a permission the API does not enforce or miss one it does.
 */
export function RolesTab() {
  const permissions = Object.keys(PERMISSIONS) as Permission[];
  // Only the member counts come from the API. The matrix itself stays derived from the catalogue,
  // which is compared to the C# one in both directions — a count is a fact about this tenant, a
  // permission is a fact about the product.
  const { data: roles } = useRoles();

  return (
    <div className="flex flex-col gap-4">
      <SectionCard title="Roles and permissions" flush>
        <DataTable
          aria-label="Which roles hold which permissions"
          size="sm"
          enableSorting={false}
          columns={matrixColumns(roles)}
          data={permissions}
          getRowId={(permission) => permission}
          pagination={{ mode: "manual", show: false }}
        />

        <p className="text-muted-foreground border-border border-t px-4 py-3 text-xs">
          Changing role membership needs the Administrator role. Reopening a closed period is
          deliberately not held by the Accountant role: closing is routine month-end work, reopening
          undoes a figure somebody has reported.
        </p>
      </SectionCard>
    </div>
  );
}

/** The permission column, then one column per role, headed by the role and how many hold it. */
function matrixColumns(roles: RoleDefinition[] | undefined): Array<ColumnDef<Permission>> {
  return [
    {
      id: "permission",
      header: "Permission",
      // The row's header, so a screen reader going down a role column keeps hearing which
      // permission each "yes" or "no" belongs to.
      meta: { rowHeader: true },
      cell: ({ row: { original: permission } }) => (
        <span className="text-foreground">
          {PERMISSIONS[permission]}
          <span className="text-fg-hint num ml-2 text-xs">{permission}</span>
        </span>
      ),
    },
    ...ROLE_NAMES.map((role): ColumnDef<Permission> => ({
      id: role,
      meta: { textAlign: "center" },
      header: () => {
        const members = roles?.find((definition) => definition.name === role)?.members;
        return (
          <span>
            {role}
            {members === undefined ? null : (
              <span className="text-fg-hint num ml-1 text-xs">{members}</span>
            )}
          </span>
        );
      },
      cell: ({ row: { original: permission } }) => {
        const held = ROLE_PERMISSIONS[role].includes(permission);
        return (
          <>
            {/* The icon is decorative; the cell's accessible name carries the answer, so a
                  screen reader hears "Accountant, no" under the permission's row. */}
            <span className="sr-only">{held ? "yes" : "no"}</span>
            <span aria-hidden="true" className={held ? "text-ok-text" : "text-muted-foreground"}>
              <Icon name={held ? "Check" : "Minus"} size="sm" />
            </span>
          </>
        );
      },
    })),
  ];
}
